import { NativeSelect } from "@chakra-ui/react"
import type { ChangeEventHandler, ComponentProps, ReactNode } from "react"
import { forwardRef } from "react"
import Pathicon from "../pathicon/Pathicon"
import Field, { FieldProps } from "./Field"

export type SelectFieldOption = {
  value: string | number
  label: ReactNode
  disabled?: boolean
}

export type SelectFieldProps = Omit<
  FieldProps,
  "as" | "children" | "disabled" | "onChange" | "value"
> & {
  disabled?: boolean
  onChange?: ChangeEventHandler<HTMLSelectElement>
  options?: SelectFieldOption[]
  placeholder?: ReactNode
  rootProps?: Omit<ComponentProps<typeof NativeSelect.Root>, "children">
  value?: string | number | readonly string[]
  children?: ReactNode
}

type SelectFieldInputProps = ComponentProps<typeof NativeSelect.Field> & {
  children?: ReactNode
  optionChildren?: ReactNode
  options?: SelectFieldOption[]
  placeholder?: ReactNode
}

const SelectFieldInput = forwardRef<HTMLSelectElement, SelectFieldInputProps>(
  function SelectFieldInput(
    { children, optionChildren, options, placeholder, ...props },
    ref
  ) {
    return (
      <NativeSelect.Field ref={ref} {...props}>
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options?.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
        {optionChildren || children}
      </NativeSelect.Field>
    )
  }
)

const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  function SelectField(
    {
      children,
      disabled,
      onChange,
      options,
      placeholder,
      rootProps,
      value,
      ...props
    },
    ref
  ) {
    const fieldProps = {
      ...props,
      disabled,
      onChange,
      optionChildren: children,
      options,
      placeholder,
      value
    }

    return (
      <NativeSelect.Root {...rootProps}>
        <Field
          color="fg"
          bg="bg"
          rounded
          ref={ref as any}
          as={SelectFieldInput as any}
          {...(fieldProps as any)}
        />
        <NativeSelect.Indicator>
          <Pathicon icon="chevron-down" />
        </NativeSelect.Indicator>
      </NativeSelect.Root>
    )
  }
)

export default SelectField
